/**
 * Common TradingView-style indicators computed from OHLCV bars
 * (oldest → newest). Used by chart alerts.
 */

export type Bar = {
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
};

export type IndicatorDef = {
  id: string;
  label: string;
  group: "price" | "ma" | "oscillator" | "volatility" | "volume";
  /** Suggested default alert level (null = use LTP / live value). */
  defaultLevel?: number;
  /** 0–100 oscillators get a max hint in the UI. */
  bounded01?: boolean;
};

/** Source catalog shown in Create Alert (Price + TV-style indicators). */
export const INDICATOR_DEFS: IndicatorDef[] = [
  { id: "price", label: "Price", group: "price" },

  { id: "sma_20", label: "SMA (20)", group: "ma" },
  { id: "sma_50", label: "SMA (50)", group: "ma" },
  { id: "sma_100", label: "SMA (100)", group: "ma" },
  { id: "sma_200", label: "SMA (200)", group: "ma" },
  { id: "ema_9", label: "EMA (9)", group: "ma" },
  { id: "ema_21", label: "EMA (21)", group: "ma" },
  { id: "ema_50", label: "EMA (50)", group: "ma" },
  { id: "ema_200", label: "EMA (200)", group: "ma" },
  { id: "wma_20", label: "WMA (20)", group: "ma" },

  { id: "rsi_14", label: "RSI (14)", group: "oscillator", defaultLevel: 70, bounded01: true },
  { id: "stoch_k", label: "Stochastic %K (14,3)", group: "oscillator", defaultLevel: 80, bounded01: true },
  { id: "stoch_d", label: "Stochastic %D (14,3)", group: "oscillator", defaultLevel: 80, bounded01: true },
  { id: "cci_20", label: "CCI (20)", group: "oscillator", defaultLevel: 100 },
  { id: "mom_10", label: "Momentum (10)", group: "oscillator" },
  { id: "roc_12", label: "Rate of Change (12)", group: "oscillator" },
  { id: "williams_r", label: "Williams %R (14)", group: "oscillator", defaultLevel: -20, bounded01: true },
  { id: "macd", label: "MACD (12,26,9)", group: "oscillator", defaultLevel: 0 },
  { id: "macd_signal", label: "MACD Signal", group: "oscillator", defaultLevel: 0 },
  { id: "macd_hist", label: "MACD Histogram", group: "oscillator", defaultLevel: 0 },
  { id: "adx_14", label: "ADX (14)", group: "oscillator", defaultLevel: 25, bounded01: true },
  { id: "di_plus", label: "DI+ (14)", group: "oscillator", defaultLevel: 25, bounded01: true },
  { id: "di_minus", label: "DI− (14)", group: "oscillator", defaultLevel: 25, bounded01: true },
  { id: "mfi_14", label: "MFI (14)", group: "oscillator", defaultLevel: 80, bounded01: true },

  { id: "atr_14", label: "ATR (14)", group: "volatility" },
  { id: "bb_upper", label: "Bollinger Upper (20,2)", group: "volatility" },
  { id: "bb_mid", label: "Bollinger Mid (20,2)", group: "volatility" },
  { id: "bb_lower", label: "Bollinger Lower (20,2)", group: "volatility" },
  { id: "bb_width", label: "Bollinger Width % (20,2)", group: "volatility" },
  { id: "supertrend", label: "Supertrend (10,3)", group: "volatility" },

  { id: "obv", label: "On Balance Volume", group: "volume" },
  { id: "vwap", label: "VWAP", group: "volume" },
];

const BY_ID: Record<string, IndicatorDef> = {};
INDICATOR_DEFS.forEach(function (d) {
  BY_ID[d.id] = d;
});

export function indicatorDef(id: string): IndicatorDef | undefined {
  return BY_ID[id] || (id === "rsi" ? BY_ID.rsi_14 : undefined);
}

export function indicatorLabel(id: string): string {
  const d = indicatorDef(id);
  return d ? d.label : id === "price" ? "Price" : String(id || "Price");
}

function closesOf(bars: Bar[]): number[] {
  return bars.map(function (b) {
    return Number(b.close);
  });
}

function smaLast(vals: number[], period: number): number | null {
  if (vals.length < period) return null;
  let s = 0;
  for (let i = vals.length - period; i < vals.length; i++) s += vals[i];
  return s / period;
}

function emaSeries(vals: number[], period: number): number[] {
  const out: number[] = [];
  if (vals.length < period) return out;
  const k = 2 / (period + 1);
  let ema = 0;
  for (let i = 0; i < period; i++) ema += vals[i];
  ema /= period;
  out[period - 1] = ema;
  for (let i = period; i < vals.length; i++) {
    ema = vals[i] * k + ema * (1 - k);
    out[i] = ema;
  }
  return out;
}

function emaLast(vals: number[], period: number): number | null {
  const s = emaSeries(vals, period);
  if (!s.length) return null;
  for (let i = s.length - 1; i >= 0; i--) {
    if (s[i] != null && isFinite(s[i])) return s[i];
  }
  return null;
}

/** Wilder RSI last value. */
export function calcRsi(closes: number[], period = 14): number | null {
  if (!closes || closes.length < period + 1) return null;
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const d = closes[i] - closes[i - 1];
    if (d >= 0) avgGain += d;
    else avgLoss -= d;
  }
  avgGain /= period;
  avgLoss /= period;
  for (let i = period + 1; i < closes.length; i++) {
    const d = closes[i] - closes[i - 1];
    const g = d > 0 ? d : 0;
    const l = d < 0 ? -d : 0;
    avgGain = (avgGain * (period - 1) + g) / period;
    avgLoss = (avgLoss * (period - 1) + l) / period;
  }
  if (avgLoss <= 1e-12) return 100;
  const rs = avgGain / avgLoss;
  return Math.round((100 - 100 / (1 + rs)) * 100) / 100;
}

function stochLast(bars: Bar[], kPeriod = 14, dPeriod = 3): { k: number; d: number } | null {
  if (bars.length < kPeriod + dPeriod - 1) return null;
  const ks: number[] = [];
  for (let i = kPeriod - 1; i < bars.length; i++) {
    let hi = -Infinity;
    let lo = Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      hi = Math.max(hi, bars[j].high);
      lo = Math.min(lo, bars[j].low);
    }
    const den = hi - lo;
    ks.push(den <= 1e-12 ? 50 : ((bars[i].close - lo) / den) * 100);
  }
  if (ks.length < dPeriod) return null;
  const k = ks[ks.length - 1];
  let dSum = 0;
  for (let i = ks.length - dPeriod; i < ks.length; i++) dSum += ks[i];
  return { k: Math.round(k * 100) / 100, d: Math.round((dSum / dPeriod) * 100) / 100 };
}

function cciLast(bars: Bar[], period = 20): number | null {
  if (bars.length < period) return null;
  const tps: number[] = [];
  for (let i = 0; i < bars.length; i++) {
    tps.push((bars[i].high + bars[i].low + bars[i].close) / 3);
  }
  const slice = tps.slice(tps.length - period);
  const mean = slice.reduce((a, b) => a + b, 0) / period;
  const md = slice.reduce((a, b) => a + Math.abs(b - mean), 0) / period;
  if (md <= 1e-12) return 0;
  return Math.round(((tps[tps.length - 1] - mean) / (0.015 * md)) * 100) / 100;
}

function atrLast(bars: Bar[], period = 14): number | null {
  if (bars.length < period + 1) return null;
  const trs: number[] = [];
  for (let i = 1; i < bars.length; i++) {
    const h = bars[i].high;
    const l = bars[i].low;
    const pc = bars[i - 1].close;
    trs.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  if (trs.length < period) return null;
  let atr = 0;
  for (let i = 0; i < period; i++) atr += trs[i];
  atr /= period;
  for (let i = period; i < trs.length; i++) {
    atr = (atr * (period - 1) + trs[i]) / period;
  }
  return Math.round(atr * 10000) / 10000;
}

function bbLast(closes: number[], period = 20, mult = 2): { upper: number; mid: number; lower: number } | null {
  if (closes.length < period) return null;
  const slice = closes.slice(closes.length - period);
  const mid = slice.reduce((a, b) => a + b, 0) / period;
  let varSum = 0;
  for (let i = 0; i < slice.length; i++) {
    const d = slice[i] - mid;
    varSum += d * d;
  }
  const sd = Math.sqrt(varSum / period);
  return {
    mid,
    upper: mid + mult * sd,
    lower: mid - mult * sd,
  };
}

function macdLast(closes: number[]): { macd: number; signal: number; hist: number } | null {
  if (closes.length < 35) return null;
  const e12 = emaSeries(closes, 12);
  const e26 = emaSeries(closes, 26);
  const macdLine: number[] = [];
  for (let i = 0; i < closes.length; i++) {
    if (e12[i] == null || e26[i] == null) continue;
    macdLine[i] = e12[i] - e26[i];
  }
  // Build dense macd series for signal EMA
  const dense: number[] = [];
  const idxMap: number[] = [];
  for (let i = 0; i < macdLine.length; i++) {
    if (macdLine[i] != null && isFinite(macdLine[i])) {
      dense.push(macdLine[i]);
      idxMap.push(i);
    }
  }
  if (dense.length < 9) return null;
  const sig = emaSeries(dense, 9);
  const lastDense = dense.length - 1;
  const macd = dense[lastDense];
  const signal = sig[lastDense];
  if (signal == null || !isFinite(signal)) return null;
  return {
    macd: Math.round(macd * 10000) / 10000,
    signal: Math.round(signal * 10000) / 10000,
    hist: Math.round((macd - signal) * 10000) / 10000,
  };
}

function williamsR(bars: Bar[], period = 14): number | null {
  if (bars.length < period) return null;
  let hi = -Infinity;
  let lo = Infinity;
  for (let i = bars.length - period; i < bars.length; i++) {
    hi = Math.max(hi, bars[i].high);
    lo = Math.min(lo, bars[i].low);
  }
  const den = hi - lo;
  if (den <= 1e-12) return -50;
  const close = bars[bars.length - 1].close;
  return Math.round(((hi - close) / den) * -100 * 100) / 100;
}

function obvLast(bars: Bar[]): number | null {
  if (bars.length < 2) return null;
  let obv = 0;
  for (let i = 1; i < bars.length; i++) {
    const vol = Number(bars[i].volume) || 0;
    if (bars[i].close > bars[i - 1].close) obv += vol;
    else if (bars[i].close < bars[i - 1].close) obv -= vol;
  }
  return obv;
}

/**
 * Evaluate current indicator value for alert source id.
 * `ltp` optionally patches the last close for live freshness.
 */
export function evalIndicator(
  source: string,
  barsIn: Bar[],
  ltp?: number
): number | null {
  const src = source === "rsi" ? "rsi_14" : source;
  if (!barsIn || !barsIn.length) {
    if (src === "price" && ltp != null && ltp > 0) return ltp;
    return null;
  }

  const bars = barsIn.slice();
  if (ltp != null && ltp > 0) {
    const last = { ...bars[bars.length - 1], close: ltp };
    if (last.high < ltp) last.high = ltp;
    if (last.low > ltp) last.low = ltp;
    bars[bars.length - 1] = last;
  }

  if (src === "price") {
    return ltp != null && ltp > 0 ? ltp : bars[bars.length - 1].close;
  }

  const closes = closesOf(bars);

  if (src === "sma_20") return smaLast(closes, 20);
  if (src === "sma_50") return smaLast(closes, 50);
  if (src === "sma_200") return smaLast(closes, 200);
  if (src === "ema_9") return emaLast(closes, 9);
  if (src === "ema_21") return emaLast(closes, 21);
  if (src === "ema_50") return emaLast(closes, 50);

  if (src === "rsi_14") return calcRsi(closes, 14);

  if (src === "stoch_k" || src === "stoch_d") {
    const s = stochLast(bars);
    if (!s) return null;
    return src === "stoch_k" ? s.k : s.d;
  }

  if (src === "cci_20") return cciLast(bars, 20);
  if (src === "mom_10") {
    if (closes.length < 11) return null;
    return Math.round((closes[closes.length - 1] - closes[closes.length - 11]) * 100) / 100;
  }
  if (src === "roc_12") {
    if (closes.length < 13) return null;
    const prev = closes[closes.length - 13];
    if (!(prev > 0)) return null;
    return Math.round(((closes[closes.length - 1] - prev) / prev) * 10000) / 100;
  }
  if (src === "williams_r") return williamsR(bars, 14);

  if (src === "macd" || src === "macd_signal" || src === "macd_hist") {
    const m = macdLast(closes);
    if (!m) return null;
    if (src === "macd") return m.macd;
    if (src === "macd_signal") return m.signal;
    return m.hist;
  }

  if (src === "atr_14") return atrLast(bars, 14);

  if (src === "bb_upper" || src === "bb_mid" || src === "bb_lower") {
    const b = bbLast(closes, 20, 2);
    if (!b) return null;
    if (src === "bb_upper") return b.upper;
    if (src === "bb_mid") return b.mid;
    return b.lower;
  }

  if (src === "obv") return obvLast(bars);

  if (src === "sma_100") return smaLast(closes, 100);
  if (src === "ema_200") return emaLast(closes, 200);
  if (src === "wma_20") return wmaLast(closes, 20);

  if (src === "adx_14" || src === "di_plus" || src === "di_minus") {
    const d = adxLast(bars, 14);
    if (!d) return null;
    if (src === "adx_14") return d.adx;
    if (src === "di_plus") return d.plusDi;
    return d.minusDi;
  }

  if (src === "mfi_14") return mfiLast(bars, 14);
  if (src === "vwap") return vwapLast(bars);
  if (src === "bb_width") {
    const b = bbLast(closes, 20, 2);
    if (!b || !(b.mid > 0)) return null;
    return Math.round(((b.upper - b.lower) / b.mid) * 10000) / 100;
  }
  if (src === "supertrend") return supertrendLast(bars, 10, 3);

  return null;
}

function wmaLast(vals: number[], period: number): number | null {
  if (vals.length < period) return null;
  let s = 0;
  let w = 0;
  for (let i = 0; i < period; i++) {
    const weight = i + 1;
    s += vals[vals.length - period + i] * weight;
    w += weight;
  }
  return w > 0 ? s / w : null;
}

function adxLast(bars: Bar[], period = 14): { adx: number; plusDi: number; minusDi: number } | null {
  if (bars.length < period * 2 + 1) return null;
  const plusDM: number[] = [];
  const minusDM: number[] = [];
  const tr: number[] = [];
  for (let i = 1; i < bars.length; i++) {
    const up = bars[i].high - bars[i - 1].high;
    const down = bars[i - 1].low - bars[i].low;
    plusDM.push(up > down && up > 0 ? up : 0);
    minusDM.push(down > up && down > 0 ? down : 0);
    const h = bars[i].high;
    const l = bars[i].low;
    const pc = bars[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  if (tr.length < period) return null;
  let atr = 0;
  let pDM = 0;
  let mDM = 0;
  for (let i = 0; i < period; i++) {
    atr += tr[i];
    pDM += plusDM[i];
    mDM += minusDM[i];
  }
  atr /= period;
  pDM /= period;
  mDM /= period;
  const dxArr: number[] = [];
  for (let i = period; i < tr.length; i++) {
    atr = (atr * (period - 1) + tr[i]) / period;
    pDM = (pDM * (period - 1) + plusDM[i]) / period;
    mDM = (mDM * (period - 1) + minusDM[i]) / period;
    const plusDi = atr > 0 ? (100 * pDM) / atr : 0;
    const minusDi = atr > 0 ? (100 * mDM) / atr : 0;
    const sum = plusDi + minusDi;
    dxArr.push(sum > 0 ? (100 * Math.abs(plusDi - minusDi)) / sum : 0);
  }
  if (dxArr.length < period) return null;
  let adx = 0;
  for (let i = 0; i < period; i++) adx += dxArr[i];
  adx /= period;
  for (let i = period; i < dxArr.length; i++) {
    adx = (adx * (period - 1) + dxArr[i]) / period;
  }
  const lastPlus = atr > 0 ? (100 * pDM) / atr : 0;
  const lastMinus = atr > 0 ? (100 * mDM) / atr : 0;
  return {
    adx: Math.round(adx * 100) / 100,
    plusDi: Math.round(lastPlus * 100) / 100,
    minusDi: Math.round(lastMinus * 100) / 100,
  };
}

function mfiLast(bars: Bar[], period = 14): number | null {
  if (bars.length < period + 1) return null;
  let pos = 0;
  let neg = 0;
  for (let i = bars.length - period; i < bars.length; i++) {
    const tp = (bars[i].high + bars[i].low + bars[i].close) / 3;
    const prevTp = (bars[i - 1].high + bars[i - 1].low + bars[i - 1].close) / 3;
    const raw = tp * (Number(bars[i].volume) || 0);
    if (tp > prevTp) pos += raw;
    else if (tp < prevTp) neg += raw;
  }
  if (neg <= 1e-12) return 100;
  const mr = pos / neg;
  return Math.round((100 - 100 / (1 + mr)) * 100) / 100;
}

function vwapLast(bars: Bar[]): number | null {
  if (!bars.length) return null;
  let pv = 0;
  let vol = 0;
  for (let i = 0; i < bars.length; i++) {
    const v = Number(bars[i].volume) || 0;
    const tp = (bars[i].high + bars[i].low + bars[i].close) / 3;
    if (v > 0) {
      pv += tp * v;
      vol += v;
    }
  }
  if (!(vol > 0)) {
    // No volume (indices): fall back to typical-price average
    let s = 0;
    for (let i = 0; i < bars.length; i++) s += (bars[i].high + bars[i].low + bars[i].close) / 3;
    return s / bars.length;
  }
  return pv / vol;
}

function supertrendLast(bars: Bar[], period = 10, mult = 3): number | null {
  if (bars.length < period + 2) return null;
  const atr = atrLast(bars, period);
  if (atr == null) return null;
  const last = bars[bars.length - 1];
  const hl2 = (last.high + last.low) / 2;
  const upper = hl2 + mult * atr;
  const lower = hl2 - mult * atr;
  // Direction from close vs band mid — enough for alert level tracking
  const mid = (upper + lower) / 2;
  return last.close >= mid ? lower : upper;
}
